import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Rocket, Terminal, ShoppingBag, Sparkles, X, ArrowRight, ArrowLeft, Check } from "lucide-react";
import axios from "axios";
import { trackEvent } from "@/lib/posthog";

const STORAGE_KEY = "nexus_onboarding_seen_v1";
const API = process.env.REACT_APP_BACKEND_URL;

// Shared distinct_id helper - keeps the modal's funnel events consistent with
// the ones emitted from EnhancedLanding.jsx.
const getDistinctId = () => {
  const k = "nexus_funnel_distinct_id";
  let v = null;
  try { v = localStorage.getItem(k); } catch { /* ignore */ }
  if (!v) {
    v = "anon_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    try { localStorage.setItem(k, v); } catch { /* ignore */ }
  }
  return v;
};

// Dual-write: PostHog (front-end) + server-side funnel (MongoDB).
const trackFunnel = (step, properties = {}) => {
  try { trackEvent(step, properties); } catch { /* ignore */ }
  try {
    axios.post(`${API}/api/onboarding-funnel/track`, {
      distinct_id: getDistinctId(),
      step,
      properties,
    }).catch(() => {});
  } catch { /* ignore */ }
};

const STEPS = [
  {
    icon: Terminal,
    accent: "from-emerald-500 to-cyan-500",
    title: "Run code in our MicroVM sandbox",
    body:
      "Every NEXUS account gets agent-grade, rate-limited code execution — Python, Node, Bash — with zero setup. Try it in the hero section without signing up.",
    cta: "Show me the sandbox",
    action: (nav) => {
      const el = document.querySelector('[data-testid="sandbox-hero"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  },
  {
    icon: Sparkles,
    accent: "from-fuchsia-500 to-indigo-500",
    title: "Route to bleeding-edge AI",
    body:
      "Claude 4.7 Opus, Gemma 4, Vercel Open Agents, Copilot MicroVMs — one catalog, one API, live routing via the Emergent LLM key.",
    cta: "Open Next-Gen AI Studio",
    action: (nav) => nav("/next-gen-ai"),
  },
  {
    icon: ShoppingBag,
    accent: "from-amber-500 to-rose-500",
    title: "List, find, message — P2P",
    body:
      "Full peer-to-peer marketplace with geospatial search, $facet analytics, and low listing fees. MongoDB-persisted, production-ready.",
    cta: "Explore marketplace",
    action: (nav) => nav("/auctions"),
  },
];

export default function OnboardingModal() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    // Only show on landing page ("/")
    if (location.pathname !== "/") return;
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        const t = setTimeout(() => {
          setOpen(true);
          trackFunnel("onboarding_shown", { step: 0 });
        }, 1200);
        return () => clearTimeout(t);
      }
    } catch {
      /* ignore */
    }
  }, [location.pathname]);

  const close = () => {
    try {
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch {
      /* ignore */
    }
    const completed = step === STEPS.length - 1;
    trackFunnel(completed ? "onboarding_completed" : "onboarding_closed", {
      step,
      completed,
    });
    setOpen(false);
  };

  const next = () => {
    if (step < STEPS.length - 1) {
      // Map the first "Next" to signup_started so the funnel sees modal
      // walkers as intent-to-signup users, not just passive viewers.
      if (step === 0) trackFunnel("signup_started", { source: "onboarding_modal", cta: "next" });
      if (step === 1) trackFunnel("onboarding_step_1", { source: "onboarding_modal" });
      if (step === 2) trackFunnel("onboarding_step_2", { source: "onboarding_modal" });
      trackFunnel("onboarding_next", { from_step: step, to_step: step + 1 });
      setStep(step + 1);
    } else close();
  };

  const act = () => {
    trackFunnel("first_action", { source: "onboarding_modal", step, cta: STEPS[step].cta });
    STEPS[step].action(navigate);
    close();
  };

  const S = STEPS[step];
  const Icon = S.icon;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="onboarding-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={close}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.95, opacity: 0, y: 18 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 18 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="relative w-full max-w-lg rounded-3xl bg-gradient-to-br from-zinc-950 via-black to-zinc-950 border border-white/10 shadow-2xl overflow-hidden"
          >
            {/* glow */}
            <div
              className={`absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl opacity-30 bg-gradient-to-br ${S.accent}`}
            />
            <button
              data-testid="onboarding-close"
              onClick={close}
              className="absolute top-4 right-4 w-8 h-8 grid place-items-center rounded-full bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="relative p-8 sm:p-10">
              {/* Progress dots */}
              <div className="flex items-center gap-1.5 mb-6">
                {STEPS.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${
                      i === step
                        ? "w-8 bg-white"
                        : i < step
                        ? "w-4 bg-white/60"
                        : "w-4 bg-white/15"
                    }`}
                  />
                ))}
                <span className="ml-auto text-xs text-zinc-500 font-mono">
                  {step + 1} / {STEPS.length}
                </span>
              </div>

              <motion.div
                key={step}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
              >
                <div
                  className={`w-14 h-14 rounded-2xl mb-6 grid place-items-center bg-gradient-to-br ${S.accent} shadow-lg`}
                >
                  <Icon className="w-7 h-7 text-white" />
                </div>

                <div className="inline-flex items-center gap-2 mb-3 text-xs uppercase tracking-[0.15em] text-zinc-400">
                  <Rocket className="w-3.5 h-3.5" />
                  Welcome to NEXUS
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight">
                  {S.title}
                </h2>
                <p className="text-sm text-zinc-400 leading-relaxed mb-8">
                  {S.body}
                </p>

                <div className="flex items-center gap-3">
                  <button
                    data-testid="onboarding-cta"
                    onClick={act}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r ${S.accent} text-black font-semibold hover:brightness-110 transition`}
                  >
                    {S.cta}
                    <ArrowRight className="w-4 h-4" />
                  </button>

                  <div className="ml-auto flex items-center gap-1">
                    {step > 0 && (
                      <button
                        data-testid="onboarding-back"
                        onClick={() => setStep(step - 1)}
                        className="px-3 py-2 rounded-lg text-sm text-zinc-400 hover:text-white hover:bg-white/5 transition inline-flex items-center gap-1"
                      >
                        <ArrowLeft className="w-3.5 h-3.5" /> Back
                      </button>
                    )}
                    <button
                      data-testid="onboarding-next"
                      onClick={next}
                      className="px-3 py-2 rounded-lg text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition inline-flex items-center gap-1"
                    >
                      {step === STEPS.length - 1 ? (
                        <>
                          Done
                          <Check className="w-3.5 h-3.5" />
                        </>
                      ) : (
                        <>
                          Skip
                          <ArrowRight className="w-3.5 h-3.5" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
